import { Button } from "@hub/components/ui/button";
import { Input } from "@hub/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@hub/components/ui/select";
import { Textarea } from "@hub/components/ui/textarea";
import { type OrgBotInput, orgBotInputSchema } from "@hub/lib/org-bots";
import { apiFetch } from "@hub/ui/api";
import { useState } from "react";

export function OrgBotCreate({
  slug,
  onCreated,
}: {
  slug: string;
  onCreated: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    title: "",
    category: "",
    description: "",
    instructions: "",
    boundaries: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  async function create() {
    setError(null);
    const parsed = orgBotInputSchema.safeParse(form);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError(`${issue?.path.join(" ") || "Bot"}: ${issue?.message ?? "Check the details"}`);
      return;
    }
    setSaving(true);
    try {
      await apiFetch(`/api/orgs/${encodeURIComponent(slug)}/bots`, {
        method: "POST",
        body: JSON.stringify(parsed.data),
      });
      setForm({
        name: "",
        title: "",
        category: "",
        description: "",
        instructions: "",
        boundaries: "",
      });
      setOpen(false);
      await onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create bot");
    } finally {
      setSaving(false);
    }
  }
  const fields: {
    key: keyof Omit<OrgBotInput, "category">;
    label: string;
    multiline?: boolean;
    hint?: string;
  }[] = [
    { key: "name", label: "Bot name" },
    { key: "title", label: "What does it do in a few words?" },
    { key: "description", label: "Description", multiline: true },
    {
      key: "instructions",
      label: "How should it work?",
      multiline: true,
      hint: "Give the bot the instructions it needs to do its job.",
    },
    {
      key: "boundaries",
      label: "What should it avoid?",
      multiline: true,
      hint: "Describe its boundaries. Outward actions still require approval.",
    },
  ];
  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">Create an organization bot</h3>
          <p className="text-sm text-muted-foreground">
            Publish a new bot for people in this organization to add to their own Helm.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={() => setOpen(!open)}>
          {open ? "Close" : "Create bot"}
        </Button>
      </div>
      {open ? (
        <div className="space-y-4">
          {fields.map((field) => (
            <div key={field.key} className="space-y-1 text-sm">
              <label className="block font-medium" htmlFor={`new-bot-${field.key}`}>
                {field.label}
              </label>
              {field.multiline ? (
                <Textarea
                  id={`new-bot-${field.key}`}
                  value={form[field.key]}
                  onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                />
              ) : (
                <Input
                  id={`new-bot-${field.key}`}
                  value={form[field.key]}
                  onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                />
              )}
              {field.hint ? <p className="text-xs text-muted-foreground">{field.hint}</p> : null}
            </div>
          ))}
          <div className="space-y-1 text-sm">
            <label htmlFor="new-bot-category" className="block font-medium">
              Category
            </label>
            <Select
              value={form.category}
              onValueChange={(category) => setForm({ ...form, category })}
            >
              <SelectTrigger id="new-bot-category">
                <SelectValue placeholder="Choose a category" />
              </SelectTrigger>
              <SelectContent>
                {[
                  "meetings",
                  "email",
                  "delivery",
                  "contracts",
                  "sales",
                  "finance",
                  "people",
                  "research",
                  "personal",
                ].map((category) => (
                  <SelectItem key={category} value={category}>
                    {category[0]?.toUpperCase()}
                    {category.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <Button type="button" disabled={saving} onClick={create}>
            {saving ? "Publishing…" : "Publish to organization"}
          </Button>
          <p className="text-xs text-muted-foreground">
            Publishing makes this bot available to the organization. It does not add it to anyone’s
            Helm.
          </p>
        </div>
      ) : null}
    </div>
  );
}
